import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import React from "react";

import {
  RejectDropResponse,
  RejectDropRow,
  RejectDropValue,
} from "@/types/job";

import { formatCell, formatDate, formatWeek } from "./utils/helpers";
import { RejectDropOverviewCard } from "./utils/OverviewCards";

export type Props = {
  data: RejectDropResponse;
  interval: string;
};

const RejectDropAnalyticsTable: React.FC<Props> = ({ data, interval }) => {
  const { overall, reject_drop } = data;

  const allStages = React.useMemo(() => {
    const set = new Set<string>();

    reject_drop.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key !== "date" && key !== "week") {
          set.add(key);
        }
      });
    });

    return Array.from(set);
  }, [reject_drop]);

  const [sorting, setSorting] = React.useState<SortingState>([
    {
      id: interval === "daily" ? "date" : "week",
      desc: false,
    },
  ]);

  const columns = React.useMemo<ColumnDef<RejectDropRow>[]>(() => {
    const periodColumn: ColumnDef<RejectDropRow> = {
      header: interval === "daily" ? "Date" : "Week",
      id: interval === "daily" ? "date" : "week",
      accessorFn: (row) => {
        if (interval === "daily") {
          return dayjs(row.date).valueOf();
        }

        const match = row.week?.match(/\((.+) to (.+)\)/);
        return match ? dayjs(match[1]).valueOf() : 0;
      },
      cell: ({ row }) =>
        interval === "daily"
          ? formatDate(row.original.date!)
          : formatWeek(row.original.week!),
      enableSorting: true,
    };

    const stageColumns: ColumnDef<RejectDropRow>[] = allStages.map((stage) => ({
      id: stage,
      header: stage,
      cell: ({ row }) => formatCell(row.original[stage] as RejectDropValue),
    }));

    return [periodColumn, ...stageColumns];
  }, [allStages, interval]);

  const table = useReactTable({
    data: reject_drop,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div>{overall && <RejectDropOverviewCard overall={overall} />}</div>

      <div className="my-2 max-h-[420px] w-full overflow-x-auto overflow-y-auto rounded border">
        <table className="min-w-[1000px] border-collapse">
          <thead className="bg-slate-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`sticky top-0 min-w-[140px] border bg-slate-100 px-3 py-2 text-left text-xs font-semibold text-gray-700 ${
                      header.column.id === "date" || header.column.id === "week"
                        ? "left-0 z-30 shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
                        : "z-20"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-1 ${
                        header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : ""
                      }`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}

                      {header.column.getIsSorted() === "asc" && "▲"}
                      {header.column.getIsSorted() === "desc" && "▼"}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`border px-3 py-2 text-sm text-gray-800 ${
                      cell.column.id === "date" || cell.column.id === "week"
                        ? "sticky left-0 z-10 bg-white shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
                        : ""
                    }`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default RejectDropAnalyticsTable;
